// =============================================================================
// Deal Image Component
// =============================================================================
// Displays deal images using a server proxy to bypass browser restrictions
// Uses base64 data URLs for maximum cross-browser compatibility (Chrome, Edge, Firefox, Safari)
// =============================================================================

import { useState, useEffect } from 'react';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../hooks/useAuth';

interface DealImageProps {
  dealId: string;
  dealName: string;
  imageUrl?: string | null;
  className?: string;
  iconClassName?: string;
  fallbackClassName?: string;
}

/**
 * Convert ArrayBuffer to base64 string
 * Works across all browsers including Edge
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Component that fetches and displays deal images through a server proxy
 * Uses base64 data URLs instead of blob URLs for cross-browser compatibility
 */
export function DealImage({
  dealId,
  dealName,
  imageUrl,
  className = 'w-full h-full object-cover',
  iconClassName = 'h-8 w-8 text-primary-600',
  fallbackClassName = 'flex items-center justify-center bg-primary-100',
}: DealImageProps) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // If no image URL configured, skip fetching
    if (!imageUrl || !dealId) {
      setLoading(false);
      setError(false);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(false);

        // Get auth token from Zustand store (same as api.ts uses)
        const token = useAuthStore.getState().accessToken;
        if (!token) {
          throw new Error('No auth token');
        }

        // Use native fetch API for better cross-browser compatibility
        const apiBaseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/investor/deals/${dealId}/image`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        if (!isMounted) return;

        // Get content type from response
        const contentType = response.headers.get('content-type') || 'image/jpeg';

        // Read as ArrayBuffer (most compatible across browsers)
        const arrayBuffer = await response.arrayBuffer();

        if (!isMounted) return;

        // Convert to base64 data URL (works in all browsers)
        const base64 = arrayBufferToBase64(arrayBuffer);
        const dataUrl = `data:${contentType};base64,${base64}`;

        setDataUrl(dataUrl);
      } catch (err) {
        if (!isMounted) return;
        // Don't log abort errors
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Failed to load deal image:', err);
        }
        setError(true);
        setDataUrl(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchImage();

    // Cleanup
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dealId, imageUrl]);

  // No image URL configured - show fallback icon
  if (!imageUrl) {
    return (
      <div className={fallbackClassName}>
        <BuildingOfficeIcon className={iconClassName} />
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className={`${fallbackClassName} animate-pulse`}>
        <div className="w-8 h-8 bg-primary-200 rounded" />
      </div>
    );
  }

  // Error or no data URL - show fallback
  if (error || !dataUrl) {
    return (
      <div className={fallbackClassName}>
        <BuildingOfficeIcon className={iconClassName} />
      </div>
    );
  }

  // Success - show the image using base64 data URL
  return (
    <img
      src={dataUrl}
      alt={dealName}
      className={className}
      loading="lazy"
    />
  );
}

export default DealImage;
